import type * as Stitches from '@stitches/react';
import { createStitches } from '@stitches/react';
import { blue, gray, green, orange, red, yellow } from './colors';

export const { styled, css, theme, createTheme, getCssText, globalCss, keyframes, config, reset } = createStitches({
  theme: {
    colors: {
      ...gray,
      ...red,
      ...blue,
      ...green,
      ...yellow,
      ...orange,

      // Semantic colors
      hiContrast: '$gray800',
      loContrast: 'white',
      diffContrastColor: '#FFFFFF',
      diffContrastBgColor: '#000000',
      accentPrimaryBgColor: '$orange500',
      accentContrastColor: '$gray12',
      highlightPrimaryBgColor: 'rgba(255, 244, 232, 1)',
      accentSecondaryBgColor: '#FFE9D1',
      standingsLinkColor: '$gray6',
      ageRestrictTextColor: '#000000',

      statsContainerBackgroundColor: '$gray1',
      socialIconBgColor: '$gray2',
      socialIconColor: '$gray8',
      tournamentHeaderBgColor: '$gray2',
      matchRowBgColor: '$gray1',
      mainEventParticipantNameColor: '$gray12',
      secondaryMainEventParticipantNameColor: '$gray6',

      // canvas: 'hsl(0 0% 93%)',
      // panel: 'white',
      // transparentPanel: 'hsl(0 0% 0% / 97%)',
      // shadowLight: 'hsl(206 22% 7% / 35%)',
      // shadowDark: 'hsl(206 22% 7% / 20%)',
      progressBarPrimaryBgColor: '#FFFFFF',

      dividerBorderColor: '$primaryGray',
      tabBorderColor: 'rgba(217, 217, 217, 1)',
      // Odds semantic tokens
      oddsContainerPrimaryBgColor: '#FFFFFF',
      oddItemPrimaryBgColor: '$gray2',
      oddItemLabelColor: '$gray600',
      oddItemCoefficientColor: '$gray900',
      oddItemHoverColor: '$gray4',
      oddsSelectionBorderColor: '$gray7',
      oddsSelectionBackgroundColor: '$gray2',
      // Fans united tokens
      fansUnitedContainerPrimaryBgColor: '#FFFFFF',
      fansUnitedContentPrimaryBgColor: '#E6E6E6',
      fansUnitedPillHoverColor: '#F7F7F7',

      // Country logo
      countryLogoBorderColor: '$gray3',

      sportEntityContainerPrimaryBgColor: '#FFFFFF',
      standingsTooltipContainerPrimaryBgColor: '$gray10',
      standingsTooltipContainerTextColor: '$gray0',
      standingsTooltipContainerDateTextColor: '$gray6',

      borderColor: '$primaryGray',
      filtersContainerPrimaryBackgroundColor: '#FFFFFF',
      liveMatchStatusColor: '$green500',
      fullTimeMatchStatusColor: '$primaryGray',
      pillBackgroundColor: '$gray1',
      pillActiveBackgroundColor: '$orange500',
      pillTextColor: '$gray9',
      pillActiveTextColor: '#FFFFFF',
      pillBorderColor: '$gray3',
      primaryBackgroundColor: '$gray2',
      rowBackgroundColor: '#FFFFFF',
      tabRowBackgroundColor: '$gray2',
      tableRightIndicatorColor: 'rgba(0, 0, 0, 0.5)',
      highLighted: '#FFFCEA',
      surfaceBackgroundColor: '$gray1',
      sportEntityOneColor: '$surfaceBlue',
      sportEntityTwoColor: '$surfaceRed',
      sportEntityLesserStatBgColor: '$surfaceBackgroundColor',
      tooltipBackgroundColor: '$gray900',
      tooltipColor: '$loContrast',
      participantServerImageColor: '$gray500',
      rowBorderColor: '$primaryBackgroundColor',
      dropdownBorderColor: '$gray3',
      winBackgroundColor: '#B0DB75',
      loseBackgroundColor: '#F27165',
      drawBackgroundColor: '#C9C9C9',
      footballFieldTone: '$gray4',
      footballFieldLineColor: '$gray12',
      whiteTeamColorAlternative: '#F8F8F8',
      progressOneBgColor: '#FF8502',
      progressTwoBgColor: '$gray6',
      progressContainerBgColor: '$gray3',
      subHeaderBgColor: '$gray3',
      progressTextColor: '$gray6',
      progressHighlightedColor: '#000000',
      statusBadgeBgColor: '$gray4',
      statusBadgeTextColor: '$gray12',
      headerBoxShadowColor: 'rgba(0, 0, 0, 0.05)',
      // Team squad
      playerStatisticsColor: '$gray6',
      // Table team name
      playerTeamColor: '$gray6',
      // Multisport
      borderMultiSportColor: '$gray4',
      multiSportCommonColor: '$gray6',
      teamLostColor: '$gray6',
      gamePartColor: '#4CAF50',
      multiSportWinner: '#D80027',
      multiSportTextColor: '#121212',
      multisportHighlightColor: '$orange500',
      multiSportBgColor: '#FFFFFF',
      multiSportHoverColor: '$gray1',
      multiSportDropdownButtonBgColor: '$orange500',
      dropdownActiveDateBgColor: '$orange500',
      groupCountColor: '#618934',
      // Dropdown
      dropdownBtnColor: '#FFFFFF',
      dropdownActiveBgColor: '#FFF4E8',
      selectOptionBgColor: '#FFFFFF',
      selectOptionBorderColor: '$gray3',
      selectButtonSecondaryColor: '#9A9A9A',
      dropdownArrowIconColor: '#9A9A9A',
      dropdownCalendarIconColor: '#d9d9d9',

      liveIndicatorColor: '#4CAF50',
      postponedMatchColor: '#D92033',
      stakesBgColor: '$gray0',
      teamStakesBorderColor: '$gray4',
      stakesSelectionBgColor: '$gray2',
      stakesSelectedBgColor: '$gray12',
      selectedStakeColor: '$gray0',
      stakeInputBorderColor: '$orangeLight0',
      roundColor: '$gray6',

      // Tournament
      tournamentBgColor: '$gray2',

      // Standings
      relegationColor: '#d01a1a',
      relegationPlayoff: '#ff6666',
      promotion: '#1262fa',
      promotionPlayoff: '#89aff8',
      championshipPlayoff: '#89aff8',
      tierTwo: '#2c782c',
      tierTwoPlayoff: '#52cc52',
      topPlayoff: '#89aff8',
      top: '#1262fa',

      // Knockout/Playoff
      knockoutBgColor: '$gray0',
      knockoutHeaderBgColor: '$gray1',
      knockoutHeaderBorderColor: '$gray3',
      knockoutGameBgColor: '$gray1',
      knockoutGameBorderColor: '$gray3',
      knockoutAggEventsBgColor: '$gray0',
      knockoutAggEventsBorderColor: '$gray3',

      // Lineups
      lineupsHeaderContainerBgColor: '$gray4',
      collapsibleBgColor: '$gray3',
      lineupsRowEvenBgColor: '$gray0',
      lineupsRowOddBgColor: '$gray1',
      lineupsSecondaryTextColor: '$gray6',
      mobileTabsSelectedBgColor: '$gray4',
      lineupsPrimaryBackgroundColor: '$gray0',

      // Match center
      tabTextColor: '$gray12',
      tabActiveTextColor: '$gray12',
      tabBackgroundColor: '$gray3',
      tabActiveBackgroundColor: '$orangeDefault',
      tabMenuBorderColor: '$gray4',
      matchCenterPrimaryBackgroundColor: '$gray0',

      // Timeline
      secondaryHeaderTextColor: '$gray6',
      timelineTabBackgroundColor: '$gray1',
      timelineBackgroundColor: 'white',
      timelineLineColor: '$gray4',

      // Player
      playerPrimaryBackgroundColor: '$gray0',
      playerSecondaryTextColor: '$gray6',
      playerBioBackgroundColor: '$gray2',
      playerBioTextColor: '$gray12',
      playerProfileBorderColor: '$gray3',

      //Team Form
      upcomingMatch: '$gray5',
      winOutcomeBG: '$green500',
      drawOutcomeBG: '#FFB15F',
      lossOutcomeBG: '#E25160',
      overtimeBG: '#FFFF00',

      // Team
      teamPrimaryBackgroundColor: '$gray0',
      teamSecondaryTextColor: '$gray6',
      emptyTeamImage: '$gray1',

      // Standings V2
      tableHeaderBG: '$gray1',
      tableBorderColor: '$gray3',
      tableRankHighlightColor: '$lightBlue',
    },
    fonts: {
      untitled: 'Untitled Sans, -apple-system, system-ui, sans-serif',
      sans: 'Open Sans, sans-serif',
      primaryFontFamily: '$sans',
    },

    space: {
      0: '2px',
      1: '4px',
      2: '8px',
      '2_5': '12px',
      3: '16px',
      4: '20px',
      5: '24px',
      6: '32px',
      '6_5': '40px',
      7: '48px',
      8: '64px',
      9: '80px',
    },
    sizes: {
      1: '4px',
      2: '8px',
      '2_5': '12px',
      3: '16px',
      4: '20px',
      5: '24px',
      '5_5': '32px',
      6: '34px',
      '6_5': '42px',
      7: '48px',
      '7_5': '56px',
      8: '64px',
      9: '80px',
      10: '11px',
    },
    fontSizes: {
      1: '9px',
      2: '11px',
      '2_5': '12px',
      3: '14px',
      4: '16px',
      5: '20px',
      6: '24px',
      7: '28px',
      8: '32px',
      9: '36px',
    },
    radii: {
      0: '2px',
      1: '4px',
      2: '6px',
      3: '8px',
      4: '12px',
      '4_5': '16px',
      5: '24px',
      round: '50%',
      pill: '9999px',
    },
    zIndices: {
      1: '100',
      2: '200',
      3: '300',
      4: '400',
      max: '999',
    },
    invertColor: {
      filter: 'invert(0)',
    },
  },
  media: {
    bp0: '(min-width: 300px)',
    bp1: '(min-width: 420px)',
    bp2: '(min-width: 768px)',
    bp3: '(min-width: 1024px)',
    bp4: '(min-width: 1200px)',
    bp5: '(min-width: 1800px)',
    motion: '(prefers-reduced-motion)',
    hover: '(any-hover: hover)',
    dark: '(prefers-color-scheme: dark)',
    light: '(prefers-color-scheme: light)',
  },
  utils: {
    p: (value: Stitches.PropertyValue<'padding'>) => ({
      padding: value,
    }),
    pt: (value: Stitches.PropertyValue<'paddingTop'>) => ({
      paddingTop: value,
    }),
    pr: (value: Stitches.PropertyValue<'paddingRight'>) => ({
      paddingRight: value,
    }),
    pb: (value: Stitches.PropertyValue<'paddingBottom'>) => ({
      paddingBottom: value,
    }),
    pl: (value: Stitches.PropertyValue<'paddingLeft'>) => ({
      paddingLeft: value,
    }),
    px: (value: Stitches.PropertyValue<'paddingLeft'>) => ({
      paddingLeft: value,
      paddingRight: value,
    }),
    py: (value: Stitches.PropertyValue<'paddingTop'>) => ({
      paddingTop: value,
      paddingBottom: value,
    }),

    m: (value: Stitches.PropertyValue<'margin'>) => ({
      margin: value,
    }),
    mt: (value: Stitches.PropertyValue<'marginTop'>) => ({
      marginTop: value,
    }),
    mr: (value: Stitches.PropertyValue<'marginRight'>) => ({
      marginRight: value,
    }),
    mb: (value: Stitches.PropertyValue<'marginBottom'>) => ({
      marginBottom: value,
    }),
    ml: (value: Stitches.PropertyValue<'marginLeft'>) => ({
      marginLeft: value,
    }),
    mx: (value: Stitches.PropertyValue<'marginLeft'>) => ({
      marginLeft: value,
      marginRight: value,
    }),
    my: (value: Stitches.PropertyValue<'marginTop'>) => ({
      marginTop: value,
      marginBottom: value,
    }),

    ta: (value: Stitches.PropertyValue<'textAlign'>) => ({ textAlign: value }),

    fd: (value: Stitches.PropertyValue<'flexDirection'>) => ({ flexDirection: value }),
    fw: (value: Stitches.PropertyValue<'flexWrap'>) => ({ flexWrap: value }),

    ai: (value: Stitches.PropertyValue<'alignItems'>) => ({ alignItems: value }),
    ac: (value: Stitches.PropertyValue<'alignContent'>) => ({ alignContent: value }),
    jc: (value: Stitches.PropertyValue<'justifyContent'>) => ({ justifyContent: value }),
    as: (value: Stitches.PropertyValue<'alignSelf'>) => ({ alignSelf: value }),
    fg: (value: Stitches.PropertyValue<'flexGrow'>) => ({ flexGrow: value }),
    fs: (value: Stitches.PropertyValue<'flexShrink'>) => ({ flexShrink: value }),
    fb: (value: Stitches.PropertyValue<'flexBasis'>) => ({ flexBasis: value }),

    bc: (value: Stitches.PropertyValue<'backgroundColor'>) => ({
      backgroundColor: value,
    }),

    br: (value: Stitches.PropertyValue<'borderRadius'>) => ({
      borderRadius: value,
    }),
    btrr: (value: Stitches.PropertyValue<'borderTopRightRadius'>) => ({
      borderTopRightRadius: value,
    }),
    bbrr: (value: Stitches.PropertyValue<'borderBottomRightRadius'>) => ({
      borderBottomRightRadius: value,
    }),
    bblr: (value: Stitches.PropertyValue<'borderBottomLeftRadius'>) => ({
      borderBottomLeftRadius: value,
    }),
    btlr: (value: Stitches.PropertyValue<'borderTopLeftRadius'>) => ({
      borderTopLeftRadius: value,
    }),

    bs: (value: Stitches.PropertyValue<'boxShadow'>) => ({ boxShadow: value }),

    lh: (value: Stitches.PropertyValue<'lineHeight'>) => ({ lineHeight: value }),

    ox: (value: Stitches.PropertyValue<'overflowX'>) => ({ overflowX: value }),
    oy: (value: Stitches.PropertyValue<'overflowY'>) => ({ overflowY: value }),

    pe: (value: Stitches.PropertyValue<'pointerEvents'>) => ({ pointerEvents: value }),
    us: (value: Stitches.PropertyValue<'userSelect'>) => ({
      WebkitUserSelect: value,
      userSelect: value,
    }),

    userSelect: (value: Stitches.PropertyValue<'userSelect'>) => ({
      WebkitUserSelect: value,
      userSelect: value,
    }),

    size: (value: Stitches.PropertyValue<'width'>) => ({
      width: value,
      height: value,
    }),

    appearance: (value: Stitches.PropertyValue<'appearance'>) => ({
      WebkitAppearance: value,
      appearance: value,
    }),
    backgroundClip: (value: Stitches.PropertyValue<'backgroundClip'>) => ({
      WebkitBackgroundClip: value,
      backgroundClip: value,
    }),
  },
});

export type CSS = Stitches.CSS<typeof config>;

export const lightThemeStyles = {};

export const clientThemesStyles = {};

export const darkThemeStyles = {
  colors: {
    hiContrast: 'white',
    loContrast: '$gray800',
    progressBarPrimaryBgColor: '$gray500',
    highLighted: '#4C5465',
    diffContrastColor: '#000000',
    diffContrastBgColor: '#FFFFFF',
    oddsContainerPrimaryBgColor: '#1F1F1F',
    oddItemPrimaryBgColor: 'hsla(0, 0%, 100%, 0.15)',
    oddItemLabelColor: '$gray400',
    oddItemCoefficientColor: '$gray100',
    ageRestrictTextColor: '#FFFFFF',

    statsContainerBackgroundColor: '$gray9',
    socialIconBgColor: '$gray8',
    socialIconColor: '$gray4',
    tournamentHeaderBgColor: '$gray11',
    matchRowBgColor: '$gray9',
    mainEventParticipantNameColor: '$gray0',
    secondaryMainEventParticipantNameColor: '$gray6',

    // Country logo
    countryLogoBorderColor: '$gray9',

    dividerBorderColor: '$gray8',

    sportEntityContainerPrimaryBgColor: '#1F1F1F',
    standingsTooltipContainerPrimaryBgColor: '$gray8',
    standingsTooltipContainerTextColor: '$gray0',
    standingsTooltipContainerDateTextColor: '$gray5',

    oddItemHoverColor: '$gray7',
    oddsSelectionBackgroundColor: '$gray8',
    // Fans united tokens
    fansUnitedContainerPrimaryBgColor: '#0E0E10',
    fansUnitedContentPrimaryBgColor: '$gray9',
    fansUnitedPillHoverColor: '#666666',
    tabRowBackgroundColor: '$gray800',
    primaryBackgroundColor: '#000000',
    borderColor: '#FFFFFF',
    filtersContainerPrimaryBackgroundColor: '#000000',
    liveMatchStatusColor: '$green500',
    fullTimeMatchStatusColor: '$primaryGray',
    pillBackgroundColor: '$gray9',
    pillTextColor: '#FFFFFF',
    pillBorderColor: '$gray7',
    footballFieldLineColor: '$gray1',
    rowBackgroundColor: '$gray10',
    tableRightIndicatorColor: 'rgba(255, 255, 255, 0.5)',
    surfaceBackgroundColor: '$gray11',
    sportEntityOneColor: '$surfaceBlue',
    sportEntityTwoColor: '$surfaceRed',
    sportEntityLesserStatBgColor: '$surfaceBackgroundColor',
    tooltipBackgroundColor: '$gray100',
    tooltipColor: '$loContrast',
    rowBorderColor: '$primaryBackgroundColor',
    progressTwoBgColor: '$gray6',
    subHeaderBgColor: '$gray11',
    progressHighlightedColor: '$gray100',
    progressContainerBgColor: '$gray8',
    statusBadgeBgColor: '$gray8',
    statusBadgeTextColor: '$gray0',
    headerBoxShadowColor: 'rgba(0, 0, 0, 0.25)',

    // Multisport
    multiSportTextColor: '#FFFFFF',
    multiSportBgColor: '$gray11',
    multiSportHoverColor: '$gray10',
    borderMultiSportColor: '$gray8',
    // Dropdown
    dropdownBtnColor: '$gray9',
    dropdownActiveBgColor: '$gray8',
    dropdownBorderColor: '$gray7',
    selectOptionBgColor: '$gray9',
    selectOptionBorderColor: '#000000',
    dropdownArrowIconColor: '#F0F0F0',
    dropdownCalendarIconColor: '#d9d9d9',

    stakesBgColor: '#191919',
    teamStakesBorderColor: '$gray8',
    stakesSelectionBgColor: '$gray9',
    stakesSelectedBgColor: '$gray0',
    selectedStakeColor: '$gray12',

    // Tournament
    tournamentBgColor: '$gray11',

    // Knockout/Playoff
    knockoutBgColor: '$gray10',
    knockoutHeaderBgColor: '$gray11',
    knockoutHeaderBorderColor: '$gray9',
    knockoutGameBgColor: '$gray9',
    knockoutGameBorderColor: '$gray8',
    knockoutAggEventsBgColor: '$gray7',
    knockoutAggEventsBorderColor: '$gray8',

    // Lineups
    lineupsHeaderContainerBgColor: '$gray11',
    collapsibleBgColor: '$gray11',
    lineupsRowEvenBgColor: '$gray10',
    lineupsRowOddBgColor: '$gray11',
    lineupsSecondaryTextColor: '$gray6',
    mobileTabsSelectedBgColor: '$gray12',
    lineupsPrimaryBackgroundColor: '$gray10',

    // Match center
    tabTextColor: '$gray5',
    tabActiveTextColor: '$gray12',
    tabBackgroundColor: '$gray11',
    tabActiveBackgroundColor: '$orangeDefault',
    tabMenuBorderColor: '$gray10',
    matchCenterPrimaryBackgroundColor: '$gray10',

    // Timeline
    secondaryHeaderTextColor: '$gray6',
    timelineTabBackgroundColor: '$gray11',
    timelineBackgroundColor: '$gray10',
    timelineLineColor: '$gray8',

    // Player
    playerPrimaryBackgroundColor: '$gray10',
    playerSecondaryTextColor: '$gray6',
    playerBioBackgroundColor: '$gray8',
    playerBioTextColor: '$gray0',
    playerProfileBorderColor: '$gray8',

    //Team Form
    upcomingMatch: '$gray5',
    winOutcomeBG: '$green500',
    drawOutcomeBG: '#FFB15F',
    lossOutcomeBG: '#E25160',
    overtimeBG: '#FFFF00',

    // Team
    teamPrimaryBackgroundColor: '$gray10',
    teamSecondaryTextColor: '$gray6',
    emptyTeamImage: '$gray9',

    // Standings V2
    tableHeaderBG: '$gray11',
    tableBorderColor: '$gray12',
    tableRankHighlightColor: '$lightBlue',
  },
  invertColor: {
    filter: 'invert(1)',
  },
};
